// Inventory Data Mappers

import {
  InventoryItem,
  Location,
  TrackedSerial,
  TimelineItem,
  SerialDetails,
  SerialLastSale,
  InventoryMovement,
  ProductInventoryStatus,
  LocationInventory,
  InventoryStatus,
  LocationType,
  SerialStatus,
  SerialStatistics,
  MovementType
} from '../models/inventory.model';

import {
  InventoryItemDTO,
  LocationDTO,
  TrackedSerialDTO,
  SerialDetailsDTO,
  SerialLastSaleDTO,
  TimelineItemDTO,
  SerialTimelineResponseDTO,
  InventoryMovementDTO,
  ProductInventoryStatusDTO,
  LocationInventoryDTO,
  CreateStockRequestDTO,
  InventoryMovementRequestDTO,
  InventoryFiltersDTO,
  InventoryMovementFiltersDTO,
  InventoryStatisticsDTO,
  SerialStatisticsDTO
} from '../models/inventory.dto';

import {InventoryFilters, InventoryMovementFilters, AddStockForm, InventoryStatistics, SerialTimelineResponse} from '../models/inventory.model';

// DTO to Model Mappers

export const mapInventoryItemDTOToModel = (dto: InventoryItemDTO): InventoryItem => ({
  id: dto.id,
  productId: dto.product_id,
  productName: dto.product_name,
  productBarcode: dto.product_barcode,
  locationId: dto.location_id,
  locationName: dto.location_name,
  quantity: dto.quantity,
  availableQuantity: dto.available_quantity,
  reservedQuantity: dto.reserved_quantity,
  minStockLevel: dto.min_stock_level,
  maxStockLevel: dto.max_stock_level,
  status: dto.status as InventoryStatus,
  lastUpdated: dto.last_updated,
  createdAt: dto.created_at
});

export const mapLocationDTOToModel = (dto: LocationDTO): Location => ({
  id: dto.id,
  name: dto.name,
  type: dto.type as LocationType,
  description: dto.description,
  capacity: dto.capacity,
  isActive: dto.is_active,
  createdAt: dto.created_at
});

export const mapTrackedSerialDTOToModel = (dto: TrackedSerialDTO): TrackedSerial => ({
  id: dto.id,
  serialNumber: dto.serial_number,
  productId: dto.product_id,
  productName: dto.product_name || `منتج ${dto.product_id}` || 'منتج غير محدد',
  locationId: dto.location_id,
  locationName: dto.location_name || 'موقع غير محدد',
  status: dto.status as SerialStatus,
  purchaseDate: dto.purchase_date,
  warrantyExpiration: dto.warranty_expiration,
  assignedTo: dto.assigned_to,
  notes: dto.notes,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at
});

const mapSerialLastSaleDTOToModel = (dto: SerialLastSaleDTO): SerialLastSale => ({
  invoiceNumber: dto.invoice_number,
  storeName: dto.store_name,
  marketerName: dto.marketer_name,
  saleDate: dto.sale_date
});

export const mapSerialDetailsDTOToModel = (dto: SerialDetailsDTO): SerialDetails => ({
  serialNumber: dto.serial_number,
  productName: dto.product_name,
  status: dto.status,
  locationName: dto.location_name,
  createdAt: dto.created_at,
  lastSale: dto.last_sale ? mapSerialLastSaleDTOToModel(dto.last_sale) : undefined
});

export const mapTimelineItemDTOToModel = (dto: TimelineItemDTO): TimelineItem => ({
  id: dto.id,
  type: dto.type,
  typeAr: dto.type_ar,
  fromLocationName: dto.from_location_name,
  toLocationName: dto.to_location_name,
  performedByName: dto.performed_by_name || '-',
  performedAt: dto.performed_at,
  notes: dto.notes
});

export const mapSerialTimelineResponseDTOToModel = (dto: SerialTimelineResponseDTO): SerialTimelineResponse => ({
  serial: dto.serial,
  currentLocation: dto.current_location,
  timeline: (dto.timeline || []).map(mapTimelineItemDTOToModel),
  pagination: dto.pagination ? {
    page: dto.pagination.page,
    limit: dto.pagination.limit,
    total: dto.pagination.total,
    totalPages: dto.pagination.total_pages
  } : undefined
});

export const mapInventoryMovementDTOToModel = (dto: any): InventoryMovement => ({
  id: dto.id,
  type: dto.type_ar || dto.type || MovementType.STOCK_IN,
  productId: dto.product_id || '',
  productName: dto.product_name || '-',
  productBarcode: dto.product_barcode || '-',
  quantity: dto.quantity || 1,
  fromLocationId: dto.from_location_id,
  fromLocationName: dto.from_location_name || '-',
  toLocationId: dto.to_location_id,
  toLocationName: dto.to_location_name || '-',
  reason: dto.reason || dto.type_ar || 'حركة مخزون',
  referenceNumber: dto.reference_number,
  performedBy: dto.performed_by_name || '-',
  performedAt: dto.performed_at || '-',
  notes: dto.notes || '-',
  cost: dto.cost,
  serialNumbers: (() => {
    if (dto.serial_number != null && dto.serial_number !== '') {
      return [dto.serial_number];
    }
    if (Array.isArray(dto.serial_numbers) && dto.serial_numbers.length > 0) {
      return dto.serial_numbers.filter((s): s is string => typeof s === 'string');
    }
    return [];
  })()
});

export const mapLocationInventoryDTOToModel = (dto: LocationInventoryDTO): LocationInventory => ({
  locationId: dto.location_id,
  locationName: dto.location_name,
  quantity: dto.quantity,
  availableQuantity: dto.available_quantity,
  reservedQuantity: dto.reserved_quantity
});

export const mapProductInventoryStatusDTOToModel = (dto: ProductInventoryStatusDTO): ProductInventoryStatus => ({
  productId: dto.product_id,
  productName: dto.product_name,
  productBarcode: dto.product_barcode,
  // v1 fields (if present)
  totalQuantity: (dto as any).total_quantity,
  availableQuantity: (dto as any).available_quantity,
  reservedQuantity: (dto as any).reserved_quantity,
  locations: Array.isArray((dto as any).locations) ? (dto as any).locations.map(mapLocationInventoryDTOToModel) : undefined,
  lowStockAlert: (dto as any).low_stock_alert,
  outOfStockAlert: (dto as any).out_of_stock_alert,
  // v2 fields
  hasSerial: (dto as any).has_serial,
  warehouseQuantity: (dto as any).warehouse_quantity,
  marketersQuantity: (dto as any).marketers_quantity,
  storesQuantity: (dto as any).stores_quantity,
  warehouseDetails: (dto as any).warehouse_details,
  marketersDetails: (dto as any).marketers_details,
  storesDetails: (dto as any).stores_details,
  summary: (dto as any).summary
});

// Model to DTO Mappers

export const mapAddStockFormToDTO = (form: AddStockForm): CreateStockRequestDTO => ({
  product_id: form.productId,
  location_id: form.locationId,
  quantity: form.quantity,
  cost: form.cost,
  created_by: form.createdBy,
  serial_numbers: form.serialNumbers
});

export const mapInventoryStatisticsDTOToModel = (dto: InventoryStatisticsDTO): InventoryStatistics => ({
  totalProducts: dto.total_products,
  productsWithStock: dto.products_with_stock,
  outOfStock: dto.out_of_stock,
  lowStock: dto.low_stock,
  availableStock: dto.available_stock,
  totalQuantity: dto.total_quantity,
  productsNotInInventory: dto.products_not_in_inventory,
  breakdown: {
    serialProducts: dto.breakdown.serial_products,
    nonSerialProducts: dto.breakdown.non_serial_products,
    productsInInventory: dto.breakdown.products_in_inventory
  }
});

export const mapSerialStatisticsDTOToModel = (dto: SerialStatisticsDTO): SerialStatistics => ({
  totalSerials: dto.total_serials,
  availableSerials: dto.available_serials,
  assignedSerials: dto.assigned_serials,
  soldSerials: dto.sold_serials
});

export const mapInventoryMovementToDTO = (movement: Partial<InventoryMovement>): InventoryMovementRequestDTO => ({
  type: movement.type as string,
  product_id: movement.productId!,
  quantity: movement.quantity!,
  from_location_id: movement.fromLocationId,
  to_location_id: movement.toLocationId,
  reason: movement.reason!,
  reference_number: movement.referenceNumber,
  notes: movement.notes,
  cost: movement.cost,
  serial_numbers: movement.serialNumbers
});

export const mapInventoryFiltersToDTO = (filters: InventoryFilters, page = 1, limit = 20): InventoryFiltersDTO => ({
  location_id: filters.locationId,
  product_id: filters.productId,
  status: filters.status,
  min_quantity: filters.minQuantity,
  max_quantity: filters.maxQuantity,
  low_stock_only: filters.lowStockOnly,
  out_of_stock_only: filters.outOfStockOnly,
  page,
  limit
});

export const mapInventoryMovementFiltersToDTO = (
  filters: InventoryMovementFilters,
  page = 1,
  limit = 20
): InventoryMovementFiltersDTO => {

  const result = {
    type: filters.type,
    product_id: filters.productId,
    location_id: filters.locationId,
    performed_by: filters.performedBy,
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    page,
    limit
  };

  return result;
};

// Array Mappers
export const mapInventoryItemsDTOToModel = (dtos: InventoryItemDTO[]): InventoryItem[] =>
  dtos.map(mapInventoryItemDTOToModel);

export const mapLocationsDTOToModel = (dtos: LocationDTO[]): Location[] =>
  dtos.map(mapLocationDTOToModel);

export const mapTrackedSerialsDTOToModel = (dtos: TrackedSerialDTO[]): TrackedSerial[] =>
  dtos.map(mapTrackedSerialDTOToModel);

export const mapInventoryMovementsDTOToModel = (dtos: InventoryMovementDTO[]): InventoryMovement[] =>
  dtos.map(mapInventoryMovementDTOToModel);
